import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { apiErr } from "@/lib/api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out, obj = user

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("dk_token");
    if (!token) { setUser(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch {
      localStorage.removeItem("dk_token");
      setUser(false);
    }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  const finishAuth = (data) => {
    localStorage.setItem("dk_token", data.token);
    setUser(data.user);
    return data;
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      finishAuth(data);
      return { ok: true };
    } catch (e) { return { ok: false, error: apiErr(e.response?.data?.detail) }; }
  };

  const resendVerification = async () => {
    try { await api.post("/auth/resend-verification"); return { ok: true }; }
    catch (e) { return { ok: false, error: apiErr(e.response?.data?.detail) }; }
  };

  const forgotPassword = async (email) => {
    try { const { data } = await api.post("/auth/forgot-password", { email }); return { ok: true, message: data.message }; }
    catch (e) { return { ok: false, error: apiErr(e.response?.data?.detail) }; }
  };

  const resetPassword = async (token, password) => {
    try { const { data } = await api.post("/auth/reset-password", { token, password }); finishAuth(data); return { ok: true }; }
    catch (e) { return { ok: false, error: apiErr(e.response?.data?.detail) }; }
  };

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      finishAuth(data);
      return { ok: true };
    } catch (e) { return { ok: false, error: apiErr(e.response?.data?.detail) }; }
  };

  const googleAuth = async (session_id) => {
    try {
      const { data } = await api.post("/auth/google", { session_id });
      finishAuth(data);
      return { ok: true };
    } catch (e) { return { ok: false, error: apiErr(e.response?.data?.detail) }; }
  };

  const verify = async (token) => {
    try {
      const { data } = await api.post(`/auth/verify/${token}`);
      finishAuth(data);
      return { ok: true };
    } catch (e) { return { ok: false, error: apiErr(e.response?.data?.detail) }; }
  };

  const refresh = loadMe;

  const logout = async () => {
    localStorage.removeItem("dk_token");
    setUser(false);
  };

  const value = useMemo(
    () => ({ user, setUser, register, login, googleAuth, verify, refresh, logout, resendVerification, forgotPassword, resetPassword }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user]
  );

  return (
    <AuthCtx.Provider value={value}>
      {children}
    </AuthCtx.Provider>
  );
}
